import { useEffect, useState } from 'preact/hooks';
import { t } from '../lib/i18n';
import { X } from 'lucide-preact';
import { InstallCoach } from '@vibeshell/runtime';

/**
 * Bottom-sheet animated coach. iOS users see a 4-frame guide; Android users
 * see a one-tap install button backed by the deferred prompt.
 *
 * `suppressed` holds the coach back while the home prompt dock is on screen
 * — the dock is a fixed bottom element in the same z-band (chat-dock z-24),
 * so the two must never stack.
 */
export function InstallCoachUI({ suppressed = false }: { suppressed?: boolean }) {
  const [coach] = useState(() => new InstallCoach());
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'unknown'>('unknown');
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    if (suppressed) return;
    const timer = window.setInterval(() => {
      if (coach.shouldShow()) {
        setPlatform(coach.isIOSSafari() ? 'ios' : coach.isAndroidChrome() ? 'android' : 'unknown');
        setVisible(true);
        coach.recordShown();
        window.clearInterval(timer);
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [coach, suppressed]);

  useEffect(() => {
    if (!visible || platform !== 'ios') return;
    // Phase 10 — respect prefers-reduced-motion. The iOS install guide
    // is a 4-frame loop that flips every 1.4 s; for vestibular-
    // sensitive users that's a strobe. Stay on the first frame
    // (the most important step — "Tap the Share button") when the OS
    // setting asks for less motion.
    let reduced = false;
    try {
      reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    } catch {
      /* matchMedia unavailable — assume normal motion */
    }
    if (reduced) {
      setFrame(0);
      return;
    }
    const t = window.setInterval(() => setFrame((f) => (f + 1) % 4), 1400);
    return () => window.clearInterval(t);
  }, [visible, platform]);

  if (!visible || suppressed) return null;

  function dismiss() {
    coach.recordDismissal();
    setVisible(false);
  }

  async function androidInstall() {
    coach.recordTapThrough();
    await coach.triggerAndroidInstall();
    setVisible(false);
  }

  // z-index sits in the documented 20-29 "fixed bottom bars" band — the
  // coach is suppressed on the home route, so it never races the chat dock.
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[26] flex justify-center px-4 pb-[calc(env(safe-area-inset-bottom)+12px)]"
    >
      <div
        className="pointer-events-auto w-full max-w-md p-4"
        style={{
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--border)',
          background: 'var(--bg-elevated)',
          boxShadow: 'var(--shadow-lg)'
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h3
              className="text-sm font-semibold"
              style={{ color: 'var(--fg)' }}
            >
              {t('install.iosTitle')}
            </h3>
            <p className="text-xs" style={{ color: 'var(--fg-muted)' }}>
              {t('install.iosSub')}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('common.dismiss')}
            className="rounded-full p-1"
            style={{ color: 'var(--fg-subtle)' }}
          >
            <X size={16} />
          </button>
        </div>

        {platform === 'ios' && (
          <div className="mt-3 flex items-center gap-3">
            <IosCoachFrame index={frame} />
            <div
              className="flex-1 text-xs leading-snug"
              style={{ color: 'var(--fg-muted)' }}
            >
              {[t('install.iosStep1'), t('install.iosStep2'), t('install.iosStep3'), t('install.iosStep4')][frame]}
            </div>
          </div>
        )}

        {platform === 'android' && coach.hasNativePrompt() && (
          <button
            type="button"
            onClick={androidInstall}
            className="mt-3 w-full px-4 py-3 text-sm font-semibold"
            style={{
              borderRadius: 'var(--radius-lg)',
              background: 'var(--color-primary)',
              color: 'var(--color-on-primary)'
            }}
          >
            {t('install.androidCta')}
          </button>
        )}
      </div>
    </div>
  );
}

function IosCoachFrame({ index }: { index: number }) {
  const cells = ['share', 'list', 'plus', 'home'] as const;
  const label = cells[index] ?? 'share';
  return (
    <div
      className="flex h-14 w-14 shrink-0 items-center justify-center text-2xl"
      style={{ borderRadius: 'var(--radius-lg)', background: 'var(--bg-subtle)' }}
    >
      {label === 'share' && <ShareGlyph />}
      {label === 'list' && <ListGlyph />}
      {label === 'plus' && <PlusGlyph />}
      {label === 'home' && <HomeGlyph />}
    </div>
  );
}

function ShareGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-primary)]">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}
function ListGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--fg-muted)' }}>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <circle cx="3" cy="6" r="1" />
      <circle cx="3" cy="12" r="1" />
      <circle cx="3" cy="18" r="1" />
    </svg>
  );
}
function PlusGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-primary)]">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
function HomeGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-success)' }}>
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}
